import * as Immutable from "immutable";
import isDeepEqual from "stores/isDeepEqual";
import isEqualForSearch from "views/stores/isEqualForSearch";
import UnitsConfig from "views/logic/aggregationbuilder/UnitsConfig";
import Pivot, { DateType } from "./Pivot";
import Series from "./Series";
import VisualizationConfig from "./visualizations/VisualizationConfig";
import SortConfig from "./SortConfig";
import WidgetFormattingSettings from "./WidgetFormattingSettings";
import WidgetConfig from "../widgets/WidgetConfig";
export default class AggregationWidgetConfig extends WidgetConfig {
  constructor(columnPivots, rowPivots, series, sort, visualization, rollup, visualizationConfig, formattingSettings, eventAnnotation = false, units = UnitsConfig.empty()) {
    super();
    this._value = {
      columnPivots,
      rowPivots,
      series,
      sort,
      visualization,
      rollup,
      visualizationConfig,
      formattingSettings,
      eventAnnotation,
      units
    };
  }
  get rowPivots() {
    return this._value.rowPivots;
  }
  get columnPivots() {
    return this._value.columnPivots;
  }
  get series() {
    return this._value.series;
  }
  get sort() {
    return this._value.sort;
  }
  get rollup() {
    return this._value.rollup;
  }
  get visualization() {
    return this._value.visualization;
  }
  get visualizationConfig() {
    if (this._value.visualizationConfig === null) {
      return void 0;
    }
    return this._value.visualizationConfig;
  }
  get formattingSettings() {
    return this._value.formattingSettings;
  }
  get eventAnnotation() {
    return this._value.eventAnnotation;
  }
  get isTimeline() {
    var _a;
    return ((_a = this.rowPivots) == null ? void 0 : _a.length) === 1 && this.rowPivots[0].type === DateType;
  }
  get isEmpty() {
    const empty = (arr) => !arr.length;
    return empty(this.rowPivots) && empty(this.columnPivots) && empty(this.series);
  }
  get rollupForBackendQuery() {
    return this.columnPivots.length > 0 ? this.rollup : true;
  }
  get units() {
    return this._value.units;
  }
  static builder() {
    return new Builder().rowPivots([]).columnPivots([]).series([]).sort([]).eventAnnotation(false).units(UnitsConfig.empty()).rollup(false);
  }
  toBuilder() {
    return new Builder(Immutable.Map(this._value));
  }
  toJSON() {
    const {
      columnPivots,
      formattingSettings,
      rollup,
      rowPivots,
      series,
      sort,
      visualization,
      visualizationConfig,
      eventAnnotation,
      units
    } = this._value;
    return {
      column_pivots: columnPivots,
      formatting_settings: formattingSettings,
      rollup,
      row_pivots: rowPivots,
      series,
      sort,
      visualization,
      visualization_config: visualizationConfig,
      event_annotation: eventAnnotation,
      units
    };
  }
  equals(other) {
    if (other instanceof AggregationWidgetConfig) {
      return [
        "columnPivots",
        "rowPivots",
        "series",
        "sort",
        "rollup",
        "eventAnnotation",
        "visualizationConfig",
        "visualization",
        "formattingSettings",
        "units"
      ].every((key) => isDeepEqual(this[key], other[key]));
    }
    return false;
  }
  equalsForSearch(other) {
    if (other instanceof AggregationWidgetConfig) {
      return ["rowPivots", "columnPivots", "series", "sort", "rollup", "eventAnnotation", "visualizationConfig"].every(
        (key) => isEqualForSearch(this[key], other[key])
      );
    }
    return false;
  }
  static fromJSON(value) {
    const {
      column_pivots,
      formatting_settings,
      rollup,
      row_pivots,
      series,
      sort,
      visualization,
      visualization_config,
      event_annotation,
      units
    } = value;
    return new Builder().columnPivots(column_pivots.map(Pivot.fromJSON)).rowPivots(row_pivots.map(Pivot.fromJSON)).series(series.map(Series.fromJSON)).sort(sort.map(SortConfig.fromJSON)).visualization(visualization).rollup(rollup).visualizationConfig(
      visualization_config ? VisualizationConfig.fromJSON(visualization, visualization_config) : null
    ).formattingSettings(formatting_settings ? WidgetFormattingSettings.fromJSON(formatting_settings) : void 0).eventAnnotation(event_annotation).units(UnitsConfig.fromJSON(units)).build();
  }
}
class Builder {
  constructor(value = Immutable.Map()) {
    this.value = value;
  }
  columnPivots(pivots) {
    return new Builder(this.value.set("columnPivots", pivots));
  }
  rowPivots(pivots) {
    return new Builder(this.value.set("rowPivots", pivots));
  }
  series(series) {
    return new Builder(this.value.set("series", series));
  }
  sort(sorts) {
    return new Builder(this.value.set("sort", sorts));
  }
  visualization(type) {
    return new Builder(this.value.set("visualization", type));
  }
  visualizationConfig(config) {
    return new Builder(this.value.set("visualizationConfig", config));
  }
  rollup(rollup) {
    return new Builder(this.value.set("rollup", rollup));
  }
  formattingSettings(value) {
    return new Builder(this.value.set("formattingSettings", value));
  }
  eventAnnotation(value) {
    return new Builder(this.value.set("eventAnnotation", value));
  }
  units(value) {
    return new Builder(this.value.set("units", value));
  }
  build() {
    const {
      rowPivots,
      columnPivots,
      series,
      sort,
      visualization,
      rollup,
      visualizationConfig,
      formattingSettings,
      eventAnnotation,
      units
    } = this.value.toObject();
    const availableSorts = [].concat(rowPivots, columnPivots, series);
    const filteredSorts = sort.filter(
      (s) => availableSorts.find(
        (availableSort) => {
          var _a;
          return s.field === availableSort.function || ((_a = availableSort.fields) == null ? void 0 : _a.includes(s.field));
        }
      )
    );
    return new AggregationWidgetConfig(
      columnPivots,
      rowPivots,
      series,
      filteredSorts,
      visualization,
      rollup,
      visualizationConfig,
      formattingSettings,
      eventAnnotation,
      units
    );
  }
}
